import React, { useEffect, useState } from 'react';
import s from './PromtBlocks.module.css';
import IconFlipHorizontal from '../../../../shared/ui/icons/IconFlipHorizontal';
import IconFlipVertical from '../../../../shared/ui/icons/IconFlipVertical';

import { useSelector } from 'react-redux';
import {
    selectSelectedImage,
    selectImageLayout,
    selectSelectedSlotIndex,
} from '../../../../store/selectors/imageSelectors';
import { useAppDispatch } from '../../../../store/store';
import { updateSelectedImageSetting, setImageLayout } from '../../../../store/slices/imageSlice';
import { setCropMode } from '../../../../store/slices/pageSlice';
import { selectCropMode } from '../../../../store/selectors/pageSelectors';
import IconCrop from '../../../../shared/ui/icons/IconCrop';
import IconRotateRight from '../../../../shared/ui/icons/IconRotateRight';
const aspectRatios = [{ label: '3:2' }, { label: '1:1' }, { label: '4:3' }, { label: '16:9' }];

const sizePresets = [
    { size: '1080x1080', label: 'Instagram post', width: 1080, height: 1080 },
    { size: '1080x1920', label: 'Instagram story', width: 1080, height: 1920 },
    { size: '1200x630', label: 'Facebook post', width: 1200, height: 630 },
    { size: '820x312', label: 'Facebook Cover', width: 820, height: 312 },
    { size: '1200x628', label: 'Image', width: 1200, height: 628 },
    { size: '700x800', label: 'Multi-image', width: 700, height: 800 },
    { size: '500x1500', label: 'Cover', width: 500, height: 1500 },
    { size: '1000x500', label: 'Pinterest post', width: 1000, height: 500 },
    { size: '1200x627', label: 'LinkedIn post', width: 1200, height: 627 },
    { size: '1584x396', label: 'LinkedIn cover', width: 1584, height: 396 },
    { size: '3840x2160', label: '4K Quality', width: 3840, height: 2160 },
];

const iconSizes: Record<string, { width: number; height: number }> = {
    '3:2': { width: 25, height: 12 },
    '1:1': { width: 20, height: 20 },
    '4:3': { width: 25, height: 19 },
    '16:9': { width: 40, height: 15 },
};

const AdjustSizeBlock: React.FC = () => {
    const selectedImage = useSelector(selectSelectedImage);
    const cropMode = useSelector(selectCropMode);

    const dispatch = useAppDispatch();

    const imageLayout = useSelector(selectImageLayout);

    const activeRatio = imageLayout?.transformation?.presets?.aspect_ratio;

    if (!selectedImage?.image) return <p className={s.text}>No image selected</p>;

    const handleChangeFlip = (value: string) => {
        let newTransform;
        if (value === 'rotate') {
            const currentAngle = selectedImage?.image?.transformation?.rotation ?? 0;
            const newAngle = (currentAngle + 90) % 360;

            const crop = selectedImage?.image?.transformation?.crop;
            console.log('crop', crop);
            let newCrop = crop;
            if (crop) {
                newCrop = {
                    ...crop,
                    // width: crop.height,
                    // height: crop.width,
                };
            }

            newTransform = {
                ...selectedImage?.image?.transformation,
                rotation: newAngle,
                crop: newCrop,
            };
        }
        if (value === 'horizontal') {
            const currentFlip = selectedImage?.image?.transformation?.flip;
            newTransform = {
                ...selectedImage?.image?.transformation,
                flip: currentFlip === 'horizontal' ? 'none' : 'horizontal',
            };
        }
        if (value === 'vertical') {
            const currentFlip = selectedImage?.image?.transformation?.flip;
            newTransform = {
                ...selectedImage?.image?.transformation,
                flip: currentFlip === 'vertical' ? 'none' : 'vertical',
            };
        }

        dispatch(updateSelectedImageSetting({ key: 'transformation', value: newTransform }));
    };

    const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const width = Number(e.target.value) || 0;
        if (selectedImage?.image) {
            if (width > selectedImage?.image?.transformation?.width) {
                return;
            }

            dispatch(
                updateSelectedImageSetting({
                    key: 'transformation',
                    value: {
                        ...selectedImage?.image?.transformation,
                        crop: {
                            x: selectedImage?.image?.transformation?.crop?.x || 0,
                            y: selectedImage?.image?.transformation?.crop?.y || 0,
                            width,
                            height:
                                selectedImage?.image?.transformation?.crop?.height ||
                                selectedImage?.image?.transformation?.height,
                            zoom: selectedImage?.image?.transformation?.crop?.zoom || 1,
                        },
                    },
                }),
            );
        }
    };

    const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const height = Number(e.target.value) || 0;
        if (selectedImage?.image) {
            if (height > selectedImage?.image?.transformation?.height) {
                return;
            }
            dispatch(
                updateSelectedImageSetting({
                    key: 'transformation',
                    value: {
                        ...selectedImage?.image?.transformation,
                        crop: {
                            x: selectedImage?.image?.transformation?.crop?.x || 0,
                            y: selectedImage?.image?.transformation?.crop?.y || 0,
                            width:
                                selectedImage?.image?.transformation?.crop?.width ||
                                selectedImage?.image?.transformation?.width,
                            height,
                            zoom: selectedImage?.image?.transformation?.crop?.zoom || 1,
                        },
                    },
                }),
            );
        }
    };

    const handleChangeSizePreset = (size: string) => {
        const preset = sizePresets.find((preset) => preset.size === size);
        if (!preset) return;

        if (selectedImage?.image) {
            dispatch(
                updateSelectedImageSetting({
                    key: 'transformation',
                    value: {
                        ...selectedImage?.image?.transformation,
                        // width: preset.width,
                        // height: preset.height,
                        presets: {
                            ...selectedImage?.image?.transformation?.presets,
                            size: preset.size,
                        },
                    },
                }),
            );
        } else if (imageLayout) {
            dispatch(
                setImageLayout({
                    ...imageLayout,
                    transformation: {
                        ...imageLayout.transformation,
                        // width: preset.width,
                        // height: preset.height,
                        presets: {
                            ...imageLayout.transformation?.presets,
                            size: preset.size,
                        },
                    },
                }),
            );
        }
    };

    const handleChangeAspectRatio = (ratio: string) => {
        if (imageLayout) {
            dispatch(
                setImageLayout({
                    ...imageLayout,
                    transformation: {
                        ...imageLayout.transformation,
                        presets: {
                            size: 'none',
                            aspect_ratio: ratio,
                        },
                    },
                }),
            );
        }
    };

    return (
        <div className={s.container}>
            <p className={s.title}>Adjust the shape and size of the image</p>

            <div className={s.block_container}>
                <div className={s.transform_container}>
                    <p className={s.title}>Image Size:</p>
                    <div className={s.inputs_container}>
                        <div className={s.input_container}>
                            <label className={s.input_label}>W</label>
                            <input
                                value={
                                    selectedImage?.image?.transformation?.crop?.width ||
                                    selectedImage?.image?.transformation?.width
                                }
                                type="number"
                                className={s.input}
                                style={{ width: '80px', height: '18px', paddingLeft: '30px' }}
                                placeholder="820"
                                onChange={handleWidthChange}
                                max={selectedImage?.image?.transformation?.width}
                            />
                        </div>
                        <div className={s.input_container}>
                            <label className={s.input_label}>H</label>
                            <input
                                value={
                                    selectedImage?.image?.transformation?.crop?.height ||
                                    selectedImage?.image?.transformation?.height
                                }
                                type="number"
                                className={s.input}
                                style={{ width: '80px', height: '18px', paddingLeft: '30px' }}
                                placeholder="615"
                                onChange={handleHeightChange}
                                max={selectedImage?.image?.transformation?.height}
                            />
                        </div>
                    </div>
                </div>
                {selectedImage?.image && (
                    <div className={s.transform_container}>
                        <p className={s.title}>Transform Image:</p>
                        <div className={s.icon} onClick={() => handleChangeFlip('horizontal')}>
                            <IconFlipHorizontal />
                        </div>
                        <div className={s.icon} onClick={() => handleChangeFlip('vertical')}>
                            <IconFlipVertical />
                        </div>
                        <div className={s.icon} onClick={() => handleChangeFlip('rotate')}>
                            <IconRotateRight />
                        </div>
                        <div
                            className={s.icon}
                            onClick={() =>
                                dispatch(setCropMode(cropMode === 'crop' ? 'move' : 'crop'))
                            }
                        >
                            <IconCrop />
                        </div>
                    </div>
                )}
            </div>
            {!selectedImage?.image && (
                <>
                    <p className={s.title}>Aspect ratio presets</p>
                    <div className={s.block_container}>
                        <div className={s.grid_four_column_container}>
                            {aspectRatios.map((ratio) => {
                                const { width, height } = iconSizes[ratio.label] ?? {
                                    width: 25,
                                    height: 12,
                                };

                                return (
                                    <div
                                        key={ratio.label}
                                        style={{
                                            width: '62px',
                                            height: '62px',
                                            justifyContent: 'end',
                                        }}
                                        className={`${s.card} ${ratio.label === activeRatio ? s.active : ''}`}
                                        onClick={() => handleChangeAspectRatio(ratio.label)}
                                    >
                                        <div
                                            className={s.icon}
                                            style={{
                                                border: '1px dashed rgba(48, 48, 48, 0.50)',
                                                width: `${width}px`,
                                                height: `${height}px`,
                                                marginBottom: '5px',
                                            }}
                                        />
                                        <p>{ratio.label}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
            {selectedImage?.image && (
                <>
                    <p className={s.title}>Size presets</p>
                    <div className={s.block_container}>
                        <div className={s.grid_four_column_container}>
                            {sizePresets.map((preset) => (
                                <div
                                    key={preset.size}
                                    style={{ width: '62px', height: '62px' }}
                                    className={`${s.card} ${
                                        preset.size ===
                                        (selectedImage?.image
                                            ? selectedImage?.image?.transformation?.presets?.size
                                            : imageLayout?.transformation?.presets?.size)
                                            ? s.active
                                            : ''
                                    }`}
                                    onClick={() => handleChangeSizePreset(preset.size)}
                                >
                                    <span className={s.sizeText}>{preset.size}</span>
                                    <span className={s.presetLabel}>{preset.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdjustSizeBlock;
